// EmailSyncSetup — self-serve wizard for the AppFolio→Field Radar email
// sync. Shows the tenant's forwarding address, per-client filter recipes,
// live stats, and a "test paste" box so the admin can verify the parser
// works before setting up their inbox rule.
import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Envelope, Sparkle, ArrowsClockwise, Copy, CheckCircle, Warning,
  ClockCounterClockwise, Info, PaperPlaneTilt, GoogleLogo, MicrosoftOutlookLogo,
} from "@phosphor-icons/react";

export default function EmailSyncSetup() {
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState("gmail");
  const [copied, setCopied]   = useState("");
  const [pasteText, setPasteText] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/integrations/email-sync/status");
      setCfg(r.data);
    } catch (e) { toast.error("Could not load status"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = async (val, key) => {
    try { await navigator.clipboard.writeText(val); setCopied(key);
      toast.success("Copied"); setTimeout(() => setCopied(""), 1500);
    } catch { toast.error("Copy failed — long-press to copy"); }
  };

  const runTest = async () => {
    if (!pasteText.trim() || pasteText.trim().length < 30) {
      toast.error("Paste a real AppFolio email body first"); return;
    }
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/integrations/intake/email", {
        from_email: "noreply@appfolio.com",
        subject: `${cfg.tenant_tag} Test WO from paste wizard`,
        text: pasteText,
      });
      setTestResult(r.data);
      toast.success(`Parsed → ${r.data.action || "ok"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Test failed"));
      setTestResult({ error: e.response?.data?.detail || "Failed" });
    } finally { setTesting(false); }
  };

  if (loading) return <AdminShell><div className="p-6 text-slate-500">Loading…</div></AdminShell>;
  if (!cfg)     return <AdminShell><div className="p-6 text-red-600">Could not load</div></AdminShell>;

  const recipe = cfg.recipes[tab];
  const forwardAddress = tab === "gmail" ? recipe.plus_address : cfg.intake_email;

  return (
    <AdminShell>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4" data-testid="email-sync-setup">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-950 p-6 sm:p-8 text-white">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-6 right-6"><Envelope weight="fill" size={200}/></div>
          </div>
          <div className="relative">
            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-200 inline-flex items-center gap-1.5">
              <Sparkle weight="fill" size={11}/> Zero-code sync · runs in your inbox
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight mt-2 tracking-tight" data-testid="email-sync-title">
              Sync AppFolio updates<br/>
              <span className="text-indigo-300">by email — no keys, no wait.</span>
            </h1>
            <p className="mt-3 text-sm text-indigo-100/85 max-w-lg">
              Set up a forwarding rule once — Field Radar receives every AppFolio WO update in parallel with ClickUp. Status, vendor, and schedule refresh automatically. Your existing setup stays untouched.
            </p>
          </div>
        </div>

        {/* Live stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="email-sync-stats">
          <Stat label="Created by email" value={cfg.stats.wos_created_by_email} accent="emerald"/>
          <Stat label="Updated by email" value={cfg.stats.wos_updated_by_email} accent="cyan"/>
          <Stat label="This month"       value={cfg.stats.wos_synced_this_month} accent="indigo"/>
          <Stat label="Last sync" value={cfg.stats.last_sync_at ? niceDate(cfg.stats.last_sync_at) : "—"} accent="slate"/>
        </div>
        {cfg.stats.last_sync_from && (
          <div className="text-[11px] text-slate-500 -mt-2 flex items-center gap-1.5">
            <ClockCounterClockwise size={11}/> Last email received from <b className="text-slate-700">{cfg.stats.last_sync_from}</b>
          </div>
        )}

        {/* Config values (copy-paste ready) */}
        <div className="rounded-xl bg-white border-2 border-slate-200 p-4 space-y-3" data-testid="email-sync-config">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            Step 1 · Your inbound address + tag
          </div>
          <ConfigRow label="Forwarding address"    value={forwardAddress}   onCopy={() => copy(forwardAddress, "addr")}    copied={copied === "addr"}/>
          <ConfigRow label="Subject-line tag"      value={cfg.tenant_tag}   onCopy={() => copy(cfg.tenant_tag, "tag")}      copied={copied === "tag"}/>
          <div className="text-[11px] text-slate-500 leading-snug flex items-start gap-1.5 mt-2 bg-slate-50 rounded-lg p-2">
            <Info size={12} className="mt-0.5 shrink-0 text-slate-400"/>
            <span>
              Gmail's plus-addressing (<code className="bg-white px-1 py-0.5 rounded text-[10px]">intake+FR-XXX</code>) tells Field Radar which company this is for — no subject rewriting needed. Outlook uses the tag in the subject line instead.
            </span>
          </div>
        </div>

        {/* Recipe tabs */}
        <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="email-sync-recipe">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step 2 · Pick your email client</div>
            <div className="ml-auto flex gap-1">
              <TabBtn active={tab === "gmail"}   onClick={() => setTab("gmail")}   testid="tab-gmail"   icon={<GoogleLogo weight="fill" size={14}/>}>Gmail</TabBtn>
              <TabBtn active={tab === "outlook"} onClick={() => setTab("outlook")} testid="tab-outlook" icon={<MicrosoftOutlookLogo weight="fill" size={14}/>}>Outlook</TabBtn>
            </div>
          </div>
          <div className="p-4">
            <div className="font-black text-sm mb-3">{recipe.title}</div>
            <ol className="space-y-2 text-sm text-slate-700">
              {recipe.steps.map((s, i) => (
                <li key={i} className="flex gap-2" data-testid={`recipe-step-${i}`}>
                  <span className="shrink-0 h-5 w-5 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                  <span className="pt-0.5" dangerouslySetInnerHTML={{ __html: s
                    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-[11px]">$1</code>')
                    .replace(/(intake[^ ]*@fieldradar\.pro|\[FR-[A-Z0-9]+\])/g, '<b>$1</b>')
                  }}/>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Test paste */}
        <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="email-sync-test">
          <div className="px-4 py-2 border-b border-slate-100">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
              <PaperPlaneTilt size={11}/> Step 3 · Paste-and-parse test
            </div>
          </div>
          <div className="p-4 space-y-2">
            <div className="text-[11px] text-slate-500">
              Paste the body of any AppFolio WO email here. Field Radar will parse it and either create or update the matching WO — same code path a real forwarded email hits.
            </div>
            <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
              rows={6}
              placeholder="Work Order #3383-1&#10;Property: 5417 Belmar St&#10;Status: Assigned&#10;Vendor: LCV General Contractor&#10;…"
              data-testid="email-sync-paste"
              className="w-full p-2 border-2 border-slate-200 rounded-lg font-mono text-xs"/>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={runTest} disabled={testing || pasteText.trim().length < 30}
                data-testid="email-sync-test-btn"
                className="h-10 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                {testing ? <ArrowsClockwise className="animate-spin" size={13}/> : <Sparkle weight="fill" size={13}/>}
                {testing ? "Parsing…" : "Run test parse"}
              </button>
              {testResult && !testResult.error && (
                <div className="text-[11px] font-black uppercase tracking-widest px-2 py-1 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-1" data-testid="email-sync-test-result">
                  <CheckCircle weight="fill" size={11}/> {testResult.action || "ok"} · WO {testResult.external_wo_number || "—"}
                </div>
              )}
              {testResult?.error && (
                <div className="text-[11px] font-black uppercase tracking-widest px-2 py-1 rounded bg-red-100 text-red-800 inline-flex items-center gap-1">
                  <Warning weight="fill" size={11}/> {testResult.error}
                </div>
              )}
            </div>
            {testResult?.parsed && (
              <details className="text-[11px] mt-1">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-800">See what Claude extracted</summary>
                <pre className="mt-2 p-2 bg-slate-900 text-emerald-300 rounded overflow-x-auto text-[10px]">{JSON.stringify(testResult.parsed, null, 2)}</pre>
              </details>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

function niceDate(iso) {
  try { const d = new Date(iso); const mins = Math.round((Date.now() - d) / 60000);
    if (mins < 1) return "just now"; if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return "—"; }
}

function Stat({ label, value, accent }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    cyan:    "bg-cyan-50 border-cyan-200 text-cyan-900",
    indigo:  "bg-indigo-50 border-indigo-200 text-indigo-900",
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
  }[accent] || "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-display font-black text-2xl mt-0.5 truncate">{value}</div>
    </div>
  );
}

function ConfigRow({ label, value, onCopy, copied }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 w-40 shrink-0">{label}</div>
      <div className="flex-1 min-w-[180px] px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 font-mono text-xs truncate" title={value}>
        {value}
      </div>
      <button onClick={onCopy}
        data-testid={`copy-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={`h-10 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition ${
          copied ? "bg-emerald-600 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"
        }`}>
        {copied ? <CheckCircle weight="fill" size={12}/> : <Copy weight="bold" size={12}/>}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid, icon }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-8 px-3 rounded-lg text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition ${
        active ? "bg-slate-900 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
      }`}>
      {icon} {children}
    </button>
  );
}
